// Composables
import PoliticaComponent from '@/pages/PoliticaComponent.vue'
import { createRouter, createWebHistory } from 'vue-router'

// Axios
import axios from '../plugins/axios'

// Pinia and Store App
import { useAppStore } from '@/stores/app'

// Routes
import MainPage from '@/pages/MainPage.vue'
import LoginPage from '@/pages/LoginPage.vue'
import Dashboard from '@/components/MainPage/Dashboard.vue'
import ListarVaquejadasPage from '@/pages/ListarVaquejadasPage.vue'
import ComprarSenhaPage from '@/pages/ComprarSenhaPage.vue'
import EditarVaquejada from '@/pages/EditarVaquejada.vue'
import DetalhesVaquejada from '@/pages/DetalhesVaquejada.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: MainPage,
      children: [
        {
          path: '',
          name: 'ListarVaquejadasPage',
          component: ListarVaquejadasPage,
          meta: { requiresAuth: false },
        },
        {
          path: '/dashboard',
          name: 'Dashboard',
          component: Dashboard,
          meta: { requiresAuth: true },
        },
        {
          path: '/vaquejadas',
          name: 'VaquejadasPage',
          component: () => import('@/pages/VaquejadasPage.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: '/vaqueiros',
          name: 'VaqueirosPage',
          component: () => import('@/pages/VaqueirosPage.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: '/vaquejadas/:id',
          name: 'VendasPage',
          component: () => import('@/pages/VendasPage.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: '/vaquejadas/cadastro',
          name: 'CadastroVaquejada',
          component: () => import('@/pages/CadastroVaquejada.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: '/vaqueiros/cadastro',
          name: 'CadastroVaqueiro',
          component: () => import('@/pages/CadastroVaqueiro.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: '/vaqueiros/editar/:id',
          name: 'EditarVaqueiro',
          component: () => import('@/pages/CadastroVaqueiro.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: '/vaquejadas/:id/comprar-senha',
          name: 'ComprarSenhaPage',
          component: ComprarSenhaPage,
          props: {
            isAdmin: false,
          },
          meta: { requiresAuth: false },
        },
        {
          path: '/vaquejadas/:id/comprar-senha/admin',
          name: 'AdminComprarSenhaPage',
          component: () => import('@/pages/ComprarSenhaPage.vue'),
          props: {
            isAdmin: true,
          },
          meta: { requiresAuth: true },
        },
        {
          path: '/vaquejadas/:id/detalhes',
          name: 'DetalhesVaquejada',
          component: DetalhesVaquejada,
          meta: { requiresAuth: false },
        },
        {
          path: '/validar-senha',
          name: 'ValidarSenhaCpf',
          component: () => import('@/pages/ValidarSenhaCpf.vue'),
          meta: { requiresAuth: false },
        },
        {
          path: '/categorias',
          name: 'ListarCategorias',
          component: () => import('@/pages/ListarCategorias.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: '/categorias/cadastro',
          name: 'CadastroCategoria',
          component: () => import('@/pages/CadastrarCategoria.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: '/cidades',
          name: 'ListarCidades',
          component: () => import('@/pages/ListarCidades.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: '/cidades/cadastro',
          name: 'CadastrarCidade',
          component: () => import('@/pages/CadastrarCidade.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: '/admins',
          name: 'ListarAdmins',
          component: () => import('@/pages/ListarAdmins.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: '/admins/cadastro',
          name: 'CadastrarAdmin',
          component: () => import('@/pages/CadastrarAdmin.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: '/vaquejadas/:id/editar',
          name: 'EditarVaquejada',
          component: EditarVaquejada,
          meta: { requiresAuth: true },
        },
        {
          path: '/politica-de-privacidade',
          name: 'PoliticaDePrivacidade',
          component: PoliticaComponent,
          meta: { requiresAuth: false },
        }
      ],
    },
    {
      path: '/login',
      name: 'Login',
      component: LoginPage,
      meta: { requiresAuth: false },
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  const store = useAppStore();
  store.setMainLoading(true);
  const token = localStorage.getItem('token');
  try {
    // Se não houver token, define como deslogado e trata a navegação
    if (!token) {
      store.setIsLoggedIn(false);
      if (to.matched.some((record) => record.meta.requiresAuth)) {
        if (to.path !== '/login') {
          return next('/login');
        }
      }
      return next();
    }

    // Valida o token no backend
    const response = await axios.post('admins/check-login', { token }, { withCredentials: true });

    if (response.status === 200 && response.data.isValid) {
      store.setIsLoggedIn(true);

      // Se já está autenticado, evita que vá para login novamente
      if (to.path === '/login') {
        return next('/dashboard');
      }

      return next();
    } else {
      const responseRefresh = await axios.post('admins/refresh-token', { token }, { withCredentials: true });
      if (responseRefresh.status === 200) {
        localStorage.setItem('token', responseRefresh.data.token);
        store.setIsLoggedIn(true);
        return next();
      }
      store.setIsLoggedIn(false);
      if (to.matched.some((record) => record.meta.requiresAuth)) {
        if (to.path !== '/login') {
          return next('/login');
        }
      }
      return next();
    }
  } catch (e: Error | any) {
    store.setIsLoggedIn(false);
    console.error('Erro na autenticação:', e);

    // Evita redirecionamento infinito caso o login falhe
    if (to.matched.some((record) => record.meta.requiresAuth) && to.path !== '/login') {
      return next('/login');
    }
    return next();
  }
})

router.afterEach(() => {
  const store = useAppStore();
  setTimeout(() => {
    store.setMainLoading(false);
  }, 500);

  setTimeout(() => {
    window.scrollTo({
      top: 0,
      // behavior: 'smooth'
    });
  }, 600);
})

export default router
